// <ShopHeroSlider> -- the main slider at the top of a shop (Hero Swiper).
// "Main Shop Slider Source" (My Shop Pub -> slider-shop):
//   - "gallery" (default): the historical image slider (`children`), as is
//   - "swiper": the chosen Swiper Manager swiper, drawn by the one renderer
// Never two main sliders: the swiper REPLACES the image slider. If the
// swiper becomes unavailable (deactivated, deleted, or a Family swiper no
// longer shared with this shop's role) the shop falls back to its images --
// never an empty top of page. Nothing is ever deleted either way.
import React, { useEffect, useState } from "react";
import { elakayAutoText } from "../pageInside/dataElakay";
import { userPub } from "../auth";
import { useElakaySwiper } from "./ElakaySwiperBlock";
import DynamicSwiper from "./DynamicSwiper";

// The hero configuration stored on the shop's pub document (GaleriesPub).
export const heroFromPub = (pub) => ({
  source: pub?.sliderShopSource === "swiper" ? "swiper" : "gallery",
  swiperId: pub?.sliderShopSwiperId ? String(pub.sliderShopSwiperId) : null,
});

// For shop pages that do not already load the owner's pub document (the
// Provider storefront): reads the public one, "gallery" until it answers.
export const useShopHeroConfig = (ownerId, shopId = null) => {
  const [hero, setHero] = useState({ source: "gallery", swiperId: null });
  useEffect(() => {
    setHero({ source: "gallery", swiperId: null });
    if (!ownerId || typeof userPub !== "function") return undefined;
    let cancelled = false;
    // Never let the hero configuration break a shop page: any failure keeps
    // the default hero.
    new Promise((resolve) => resolve(userPub(ownerId, shopId)))
      .then((pub) => { if (!cancelled) setHero(heroFromPub(pub)); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [ownerId, shopId]);
  return hero;
};

const ShopHeroSlider = ({ hero, ownerId = null, children = null }) => {
  const swiperId = hero?.source === "swiper" ? hero.swiperId : null;
  const { swiper, loading } = useElakaySwiper(swiperId, ownerId);
  if (!swiperId) return children;
  // Reserve the space instead of flashing the image slider first.
  if (loading) return <div className="dsw-slot"><div className="dsw-hero-loading" aria-busy="true" /></div>;
  if (!swiper) return children;
  return (
    <div className="dsw-slot dsw-hero" data-shop-hero="swiper" data-elakay-swiper-id={swiperId}>
      <DynamicSwiper swiper={swiper} eager label={elakayAutoText("h_dsw_shop_hero_label", "Shop highlights")} />
    </div>
  );
};

export default ShopHeroSlider;
